import moment from "moment";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { createContext, useContext, useMemo, useState } from "react";
import {
  ApiResponseOkContext,
  AssetListContext,
  AssetReplacementListContext,
} from "../HardwareDetails/HardwareDetails";
import { Asset, ReplacementDetail } from "../../resource/bff";
import RenewalDate from "./components/RenewalDate";
import RenewalInfo from "./components/RenewalInfo";
import {
  AddressConfirmStatusEnum,
  DeliveryModeEnum,
  LaptopReplacementStatesEnum,
  StringFulBooleanValue,
} from "../../helper/constant";
import { buildHardwareReplacement } from "../../helper/helper";
import ReplacementNotice from "./components/ReplacementNotice";
import LaptopReplacementModal from "../LaptopReplacementModal/LaptopReplacementModal";
import { useGetUserContactInfoFromWDAsync } from "../LaptopReplacementModal/async/useGetUserContactInfoFromWDAsync";
import { useGetUserLocationsFromSNAsync } from "../LaptopReplacementModal/async/useGetUserLocationsFromSNAsync";

type Props = {
  fetching: boolean;
  loading: boolean;
};

export type AssetWithState = Asset & {
  requestId?: string;
  state?: LaptopReplacementStatesEnum;
  deliveryMode?: DeliveryModeEnum;
  initiatedDate?: Date;
  isAllCountryLocationShowed?: StringFulBooleanValue;
  isBuybackPreferenceEnabled?: StringFulBooleanValue;
  allowDeferReplacement?: StringFulBooleanValue;
  replacementPeriod?: string;
  buyBackLink?: string;
  details?: ReplacementDetail;
};

export const ReplacementUserContext = createContext({} as any);

export default function AssetList({ fetching, loading }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAsset, setCurrentAsset] = useState<AssetWithState>(null);
  const assets: Asset[] = useContext(AssetListContext);
  const { assetReplacements } = useContext(AssetReplacementListContext);

  const apiResponseOk = useContext(ApiResponseOkContext);
  const laptopCategory = "Computer Laptop";

  const assetsWithStatus: AssetWithState[] = useMemo(() => {
    if (loading || fetching) return [];
    const list = buildHardwareReplacement(assets, assetReplacements);
    if (currentAsset?.serialNumber) {
      const current = list.find(
        (item) => item.serialNumber === currentAsset.serialNumber
      );
      if (current) setCurrentAsset(current);
    }
    return list;
  }, [loading, fetching, assets, assetReplacements]);

  const isReplacementNoticeShow: boolean = useMemo(() => {
    let flag = false;
    assetsWithStatus.forEach((laptop) => {
      if (
        laptop.state === LaptopReplacementStatesEnum.WAITING_RESPONSE ||
        (laptop.state === LaptopReplacementStatesEnum.RESPONSE_CAPTURE &&
          laptop?.details.reconfirmationStatus ===
            AddressConfirmStatusEnum.INITIATED)
      ) {
        flag = true;
      }
    });
    return Boolean(flag);
  }, [assetsWithStatus]);

  const {
    data: contactInfo,
    isLoading: isContactInfoLoading,
    error: isContactInfoError,
  } = useGetUserContactInfoFromWDAsync(isReplacementNoticeShow);

  const {
    data: userLocations,
    isLoading: isUserLocationsLoading,
    error: isUserLocationsError,
  } = useGetUserLocationsFromSNAsync(isReplacementNoticeShow);

  const renderActionCell = (item: AssetWithState) =>
    item?.state === LaptopReplacementStatesEnum.WAITING_RESPONSE ||
    item?.state === LaptopReplacementStatesEnum.RESPONSE_CAPTURE ||
    item?.state === LaptopReplacementStatesEnum.REPLACEMENT_IN_PROGRESS ? (
      <TableCell>
        <Button
          sx={{
            color: "#BD4257",
            padding: 0,
            "&::after": {
              content: '""',
              display:
                item?.state === LaptopReplacementStatesEnum.WAITING_RESPONSE ||
                (item?.state === LaptopReplacementStatesEnum.RESPONSE_CAPTURE &&
                  item?.details.reconfirmationStatus ===
                    AddressConfirmStatusEnum.INITIATED)
                  ? "inline-block"
                  : "none",
              position: "absolute",
              right: -1,
              top: "50%",
              transform: "translateY(-50%)",
              width: "6px",
              height: "6px",
              backgroundColor: "#CC850A",
              borderRadius: "50%",
            },
          }}
          variant="text"
          onClick={() => {
            setCurrentAsset(item);
            setIsModalOpen(true);
          }}
        >
          View
        </Button>
      </TableCell>
    ) : (
      <TableCell>
        <Typography
          fontWeight={400}
          fontSize={"14px"}
          fontStyle={"Inter"}
          padding={"6px 12px"}
        >
          None
        </Typography>
      </TableCell>
    );

  return (
    <ReplacementUserContext.Provider
      value={{
        userContactInfoFromWD: contactInfo,
        isContactInfoLoading: isContactInfoLoading,
        userOfficeLocationFromSN: userLocations,
        isUserOfficeLocationsLoading: isUserLocationsLoading,
        isContactInfoError: isContactInfoError,
        isUserLocationsError: isUserLocationsError,
      }}
    >
      <LaptopReplacementModal
        key={currentAsset?.serialNumber}
        currentAsset={currentAsset}
        isModalOpen={isModalOpen}
        handleCloseModal={() => {
          setIsModalOpen(false);
          setCurrentAsset(null);
        }}
      />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant={"h3"} color={"primary.main"}>
            Assigned to me
            {!loading && fetching && (
              <CircularProgress
                size={15}
                thickness={5}
                sx={{ marginLeft: "8px", marginTop: "4px" }}
                aria-label={"loading-animation"}
              />
            )}
          </Typography>
        </Grid>
        <ReplacementNotice isShow={isReplacementNoticeShow} />
        <Grid item xs={12}>
          <TableContainer component={Paper} sx={{ maxHeight: 438 }}>
            <Table aria-label="asset table" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: "#FFF" }}>Device</TableCell>
                  <TableCell sx={{ backgroundColor: "#FFF" }}>
                    Serial Number
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#FFF" }}>
                    Date Assigned
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#FFF" }}>
                    Eligible for Renewal
                    <Typography display={"inline"} color={"red"}>
                      {" *"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#FFF" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assetsWithStatus.map((row) => (
                  <TableRow key={row.serialNumber}>
                    <TableCell component="th" scope="row">
                      <Stack direction="row" spacing={0.5}>
                        <Typography display={"inline"} fontSize={"inherit"}>
                          {row.modelName || "NA"}
                        </Typography>
                        {
                          /* nosemgrep */ process.env.SHOW_ASSIGNMENT_TYPE ==
                            "true" &&
                            row.assetCategory == laptopCategory && (
                              <Chip
                                sx={{
                                  height: "20px",
                                  padding: "1px 0px, 0px, 0px",
                                  "font-size": "12px",
                                }}
                                size="small"
                                label={row.assignmentType}
                                color={
                                  row.assignmentType == "Primary"
                                    ? "secondary"
                                    : "primary"
                                }
                                variant="outlined"
                              />
                            )
                        }
                      </Stack>
                    </TableCell>
                    <TableCell>{row.serialNumber || "NA"}</TableCell>
                    <TableCell>
                      {row.assignedDate
                        ? moment(row.assignedDate).format("MMM DD, yyyy")
                        : "NA"}
                    </TableCell>
                    <TableCell>
                      <RenewalDate
                        date={row.replacementDate}
                        isReplaceable={row.isReplaceable}
                      />
                    </TableCell>
                    {renderActionCell(row)}
                  </TableRow>
                ))}
                {!loading && assets.length == 0 && (
                  <TableRow>
                    <TableCell>
                      {apiResponseOk
                        ? "No device is assigned for you"
                        : "Oops! Looks like we’ve encountered some issue. Try refreshing the page or let us know through feedback."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {loading && (
              <Box
                p={2}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
                aria-label={"loading-animation"}
              >
                <CircularProgress />
              </Box>
            )}
          </TableContainer>
        </Grid>
        <Grid item xs={12}>
          <RenewalInfo />
        </Grid>
      </Grid>
    </ReplacementUserContext.Provider>
  );
}
